<template>
    <div
        ref="container"
        class="campaign-banner"
        :class="{
            'campaign-banner--dark': isDarkTheme,
            'campaign-banner--resizing': masterTemplate.adType !== 'INDESIGN'
        }"
    >
        <div v-resize-observer="onContentResize" class="campaign-banner__content">
            <BannerPreview
                :id="bannerId"
                :preview-values="previewValues"
                :ad-type="masterTemplate.adType"
                :playback-namespace="playbackNamespace"
            />
        </div>

        <div class="campaign-banner__footer">
            <Poptip
                placement="bottom-start"
                class="campaign-banner__footer__name-container hide-title"
                :popper-class="isDarkTheme ? 'theme-campaign-dark' : ''"
                trigger="hover"
                transfer
            >
                <span class="campaign-banner__footer__name">
                    {{ bannerName }}
                </span>

                <template #content>
                    <div>
                        <p>{{ bannerName }}</p>
                    </div>
                </template>
            </Poptip>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { vResizeObserver } from "@vueuse/components";
import type { MasterTemplate } from "hoxton-types";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import { getBannerIdFromMasterTemplate } from "@/utils/getBannerIframeId";
import { BannerNamespace } from "@/components/PlaybackControls/usePlaybackController";
import { convertIndesignDimensionToPixels, formatIndesignDimension } from "hoxton-shared/utils/indesignMeasurementUnit";

const HEADER_HEIGHT = 40;

export default defineComponent({
    components: {
        BannerPreview
    },

    directives: {
        "resize-observer": vResizeObserver
    },

    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        },
        inputHeight: {
            type: Number,
            required: true
        },
        inputWidth: {
            type: Number,
            required: true
        }
    },

    emits: ["resized"],

    data() {
        return {
            playbackNamespace: BannerNamespace.NewSizeModal
        };
    },

    computed: {
        bannerId() {
            return getBannerIdFromMasterTemplate(this.masterTemplate, this.playbackNamespace);
        },

        isDarkTheme() {
            return this.$store.state.ui.themeMode.theme === "dark";
        },

        previewValues() {
            return JSON.stringify({
                computedOverwrites: [],
                metadata: [],
                masterTemplate: {
                    _id: this.masterTemplate._id,
                    persistentKey: this.masterTemplate.persistentKey,
                    width: this.masterTemplate.width,
                    height: this.masterTemplate.height,
                    sourceDocuments:
                        this.masterTemplate.adType === "INDESIGN"
                            ? this.masterTemplate.sourceDocuments || []
                            : undefined,
                    defaultSharedOverrides:
                        this.masterTemplate.adType === "INDESIGN"
                            ? this.masterTemplate.defaultSharedOverrides || []
                            : undefined,
                    aeInfo:
                        this.masterTemplate?.adType === "AE"
                            ? {
                                  completed: this.masterTemplate?.aeInfo?.completed,
                                  plainlyProjectId: this.masterTemplate?.aeInfo?.plainlyProjectId,
                                  plainlyTemplateId: this.masterTemplate?.aeInfo?.plainlyTemplateId
                              }
                            : null,
                    reportingLabel: this.masterTemplate.reportingLabel
                },
                targetSize:
                    this.masterTemplate.adType === "INDESIGN"
                        ? {
                              width: this.inputWidth,
                              height: this.inputHeight,
                              unit: (this.masterTemplate as { measurementUnit?: string }).measurementUnit
                          }
                        : undefined,
                previewUrl: this.masterTemplate.preview,
                psdThumbnail: this.masterTemplate.adType === "PSD" ? this.masterTemplate.psdThumbnail : null
            });
        },

        bannerName() {
            if (this.masterTemplate.adType === "INDESIGN") {
                const unit = (this.masterTemplate as { measurementUnit?: string }).measurementUnit;
                const w = formatIndesignDimension(this.inputWidth, unit);
                const h = formatIndesignDimension(this.inputHeight, unit);
                return `${w} x ${h} - ${this.masterTemplate.name}`;
            }
            return `${this.inputWidth} x ${this.inputHeight} - ${this.masterTemplate.name}`;
        },

        displayWidth() {
            if (this.masterTemplate.adType === "INDESIGN") {
                const unit = (this.masterTemplate as { measurementUnit?: string }).measurementUnit;
                return convertIndesignDimensionToPixels(this.inputWidth, unit);
            }
            return this.inputWidth;
        },

        displayHeight() {
            if (this.masterTemplate.adType === "INDESIGN") {
                const unit = (this.masterTemplate as { measurementUnit?: string }).measurementUnit;
                return convertIndesignDimensionToPixels(this.inputHeight, unit);
            }
            return this.inputHeight;
        }
    },

    watch: {
        inputWidth() {
            this.resetContainerStyle();
        },

        inputHeight() {
            this.resetContainerStyle();
        }
    },

    mounted() {
        this.resetContainerStyle();
    },

    methods: {
        resetContainerStyle() {
            (this.$refs.container as HTMLDivElement).style.width = `${this.displayWidth}px`;
            (this.$refs.container as HTMLDivElement).style.height = `${this.displayHeight + HEADER_HEIGHT}px`;
        },

        onContentResize(entries: ResizeObserverEntry[]) {
            if (!entries.length) {
                return;
            }

            const entry = entries[0] as ResizeObserverEntry;
            const { contentRect } = entry;
            this.$emit("resized", { width: contentRect.width, height: contentRect.height });
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
$secondaryBackColor: #1b1f28;

.campaign-banner {
    overflow: hidden;

    border: 1px solid $cmp-light-border-color;

    display: flex;
    flex-direction: column;

    box-sizing: content-box;
}

.campaign-banner--dark {
    border-color: $darktheme40;
}

.campaign-banner--resizing {
    resize: both;
}

.campaign-banner__content {
    flex: 1 1 auto;

    display: flex;

    position: relative; // for the qa preview annotation layer
}

.campaign-banner__footer {
    color: $cmp-light-font-color;
}

.campaign-banner__footer__name {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.campaign-banner__footer__template-name {
    padding: 10px;
}

.campaign-banner__checkbox {
    width: 100%;
    height: 100%;

    position: relative;
    margin-right: 0;

    .ivu-checkbox {
        display: flex;

        position: absolute;
        top: 10px;
        left: 10px;
        font-size: 20px;

        &-inner {
            width: 20px;
            height: 20px;

            &:after {
                height: 14px;
                left: 8px;
            }
        }
    }

    .ivu-checkbox-label-text {
        margin: 0;
        margin-right: 0;
        padding: 0;
        display: flex;

        height: 100%;
    }
}

$height: 40px;
$button-width: 40px;

.campaign-banner__footer {
    width: 100%;
    height: $height;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: center;

    .campaign-banner__footer__name-container {
        .ivu-icon {
            cursor: pointer;
        }
    }

    .campaign-banner__footer__name-container {
        flex: 1 0;
        text-align: left;
        overflow: hidden;
    }

    .campaign-banner__footer__name {
        font-size: 12px;
        padding-left: 12px;
    }
}
</style>
